export default `<p><strong>文檔版本：</strong>V3.28 &nbsp;|&nbsp; <strong>發布日期：</strong>2022 年 &nbsp;|&nbsp; <strong>文檔性質：</strong>產品白皮書與安全評估摘要</p>

<h2>一、摘要</h2>
<p>本借貸池（Lending Pool）是基於去中心化金融（DeFi）貨幣市場協議構建的鏈上流動性存貸服務模組。系統通過開源智能合約與程序化利率模型，將用戶的數字資產接入成熟的鏈上借貸市場，實現<strong>存款生息、抵押借款、流動性共享</strong>等核心功能。</p>
<p>本模組不依賴人工干預進行日常資金調度，利率、容量、利用率等關鍵指標均由鏈上市場實時決定，並通過多源數據聚合與定時同步機制向用戶透明展示。本文檔旨在說明產品架構、運行機制、安全設計、風險披露及合規評估結論，供用戶與合作夥伴查閱。</p>

<h2>二、行業背景與產品定位</h2>
<p>傳統金融借貸依賴中心化機構進行信用評估與資金撮合，存在門檻高、透明度低、跨境結算成本高等問題。DeFi 借貸協議通過智能合約自動匹配供需雙方，以超額抵押與清算機制保障系統償付能力，已成為 Web3 基礎設施的重要組成部分。</p>
<p>本借貸池模組的定位為：</p>
<ul>
  <li><strong>鏈上貨幣市場入口：</strong>聚合多鏈、多協議借貸池數據，為用戶提供統一的存貸信息視圖；</li>
  <li><strong>程序化收益分配：</strong>依據授權資產餘額與市場 APY，按固定週期自動結算收益；</li>
  <li><strong>透明可驗證：</strong>池子 TVL、APY、利用率、LTV 等指標可溯源至公開鏈上數據；</li>
  <li><strong>非託管原則：</strong>用戶資產授權與鏈上交互由用戶錢包發起，平台不持有用戶私鑰。</li>
</ul>

<h2>三、系統架構</h2>
<h3>3.1 架構概覽</h3>
<p>系統採用分層架構設計，主要包含以下組件：</p>
<ul>
  <li><strong>用戶交互層：</strong>Web / 移動端界面，支持錢包連接、資產授權、池子瀏覽、收益查詢；</li>
  <li><strong>業務服務層：</strong>用戶鑑權、授權狀態管理、收益訂單生成與查詢、多語言內容展示；</li>
  <li><strong>數據聚合層：</strong>對接 DeFi 數據聚合 API 及協議 GraphQL 接口，定時同步借貸池指標；</li>
  <li><strong>鏈上協議層：</strong>基於經審計的開源借貸協議智能合約，執行實際的存借邏輯。</li>
</ul>

<h3>3.2 核心數據指標</h3>
<table>
  <thead>
    <tr><th>指標</th><th>說明</th></tr>
  </thead>
  <tbody>
    <tr><td>TVL（總鎖倉價值）</td><td>池內資產以美元計價的總量，反映市場深度</td></tr>
    <tr><td>Supply APY（存款年化）</td><td>由基礎利率與協議獎勵疊加構成，隨利用率動態浮動</td></tr>
    <tr><td>Borrow APY（借款年化）</td><td>借款人需支付的年化成本，與資金利用率正相關</td></tr>
    <tr><td>Utilization Rate（利用率）</td><td>已借出資金占可借資金的比例，影響利率曲線</td></tr>
    <tr><td>Max LTV（最高貸款價值比）</td><td>抵押品可支撐的最大借款比例，超額部分觸發清算</td></tr>
    <tr><td>Supply / Borrow Cap</td><td>單資產供給與借款上限，用於控制集中度風險</td></tr>
    <tr><td>Available Liquidity</td><td>當前可即時借出的流動性規模</td></tr>
  </tbody>
</table>

<h3>3.3 數據同步機制</h3>
<p>借貸池市場數據通過定時任務從公開數據源拉取，寫入本地緩存與歷史快照，用於前端展示、歷史曲線及運營監控。每次同步記錄時間戳，確保數據可追溯。</p>

<h2>四、用戶流程與收益機制</h2>
<h3>4.1 參與流程</h3>
<ol>
  <li><strong>連接錢包：</strong>用戶通過 Web3 錢包完成身份綁定；</li>
  <li><strong>資產授權：</strong>對 USDT、USDC 等支持資產執行鏈上 Approve 授權；</li>
  <li><strong>加入借貸池：</strong>系統根據授權狀態將用戶納入對應池子的收益計算隊列；</li>
  <li><strong>收益結算：</strong>按固定週期（每日）依據授權餘額與市場利率生成結算訂單；</li>
  <li><strong>資產查詢：</strong>用戶可在「個人資產」頁查看授權餘額、累計收益及歷史結算明細。</li>
</ol>

<h3>4.2 利率模型</h3>
<p>存款利率（Supply APY）由基礎 APY 與獎勵 APY 兩部分組成，實際到賬收益受授權餘額、市場 APY 波動、結算週期及鏈上 Gas 等因素影響。<strong>歷史 APY 不代表未來收益。</strong></p>

<h3>4.3 費用說明</h3>
<h4>DeFi 賺幣如何收費？</h4>
<p>鏈上交易需支付 gas 費用，以完成與智能合約的交互操作，包括認購和領取收益。對於部分資產，DeFi 借貸池與金庫管理方將根據您實際獲得的利潤收取一定比例的業績費，本金不受影響。</p>

<h2>五、安全設計</h2>
<ul>
  <li>底層借貸協議採用經多家安全機構審計的開源合約，核心邏輯鏈上執行；</li>
  <li>非託管架構，平台不存儲用戶私鑰或助記詞；</li>
  <li>僅請求業務所需的 Token Approve 權限，用戶可隨時在錢包中撤銷；</li>
  <li>接口鑑權、HTTPS 傳輸、後台 RBAC 權限隔離與操作審計。</li>
</ul>

<h2>六、安全評估摘要</h2>
<table>
  <thead>
    <tr><th>評估項</th><th>結論</th><th>說明</th></tr>
  </thead>
  <tbody>
    <tr><td>智能合約依賴</td><td><strong>通過</strong></td><td>採用經公開審計的 DeFi 協議合約</td></tr>
    <tr><td>非託管與密鑰管理</td><td><strong>通過</strong></td><td>無私鑰存儲，授權由用戶錢包本地簽名</td></tr>
    <tr><td>API 與鑑權安全</td><td><strong>通過</strong></td><td>接口鑑權與 HTTPS 已落實</td></tr>
    <tr><td>數據同步與一致性</td><td><strong>通過</strong></td><td>定時同步 + 快照機制，數據可溯源</td></tr>
    <tr><td>收益結算邏輯</td><td><strong>通過</strong></td><td>週期結算規則明確，訂單可追溯</td></tr>
  </tbody>
</table>
<p><strong>總體評估結論：</strong>在現有架構與運維規範下，核心安全控制措施有效。用戶仍須自行評估 DeFi 協議層風險，並妥善保管錢包私鑰。</p>

<h2>七、風險披露</h2>
<ul>
  <li><strong>智能合約風險：</strong>儘管經審計，合約仍可能存在未被發現的漏洞；</li>
  <li><strong>市場風險：</strong>APY 隨利用率與治理提案波動，可能出現大幅變化；</li>
  <li><strong>清算風險：</strong>抵押品價格劇烈下跌可能觸發清算；</li>
  <li><strong>穩定幣脫錨風險：</strong>USDT、USDC 等在極端條件下可能偏離錨定價格；</li>
  <li><strong>監管政策風險：</strong>各國對 DeFi 的監管政策仍在演進。</li>
</ul>
<p><strong>本模組不提供投資建議，也不保證任何固定收益。</strong></p>

<h2>八、術語表</h2>
<ul>
  <li><strong>APY：</strong>年化收益率；</li>
  <li><strong>TVL：</strong>總鎖倉價值；</li>
  <li><strong>LTV：</strong>貸款價值比；</li>
  <li><strong>Approve：</strong>ERC-20 代幣授權；</li>
  <li><strong>清算（Liquidation）：</strong>抵押品不足時自動出售抵押品以償還債務。</li>
</ul>

<p>— 本文檔僅供信息參考，不構成法律、財務或投資建議。鏈上數據以區塊瀏覽器及協議官方接口為準。</p>`
